import { call, put, select } from 'redux-saga/effects';
import * as shallowEqual from 'shallowequal';
import * as _ from 'underscore';

import { Messages } from '../../../../codegen/Localize';
import { Filter } from '../../../../ts/helpers/Filter';
import { Order } from '../../../../ts/helpers/Order';
import { BackgroundJobTaskType } from '../../../../ts/models/BackgroundJobInfo';
import { DatasourceDefinition, ViewDefinition, WorkbookDefinition } from '../../../../ts/models/ContentDefinitions';
import { ContentFilterKeyName, ContentFiltersKeys } from '../../../../ts/models/ContentFilters';
import { ExplorableItemType } from '../../../../ts/models/ContentType';
import { OrderKeys } from '../../../../ts/models/OrderKeys';
import { FilterFieldNames, IStringValuedFilterClause, IValuedFieldFilterClause } from '../../../../ts/ServerApi/types/IFilterClause';
import { IOrderClause } from '../../../../ts/ServerApi/types/IOrderClause';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { contentCountsUpToDate } from '../../../actions/spaces/ContentCountsActions';
import { ContentTypeFilterSelectedAction, filterContentType } from '../../../actions/spaces/ContentExplorerFilterActions';
import { ContentFilterAppliedAction, filterMixedContent, mixedContentFilterApplied, SetContentFilterNoRequestContentAction, UpdateFiltersInBothUrlAndStateAction, updateFiltersInBothUrlAndStateAction } from '../../../actions/spaces/filters/FilterActions';
import { doesActionCreateDifferentFilter, doesActionCreateSameFilter } from '../../../actions/spaces/filters/FilterHelpers';
import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import { appStore } from '../../../AppStore';
import { selectCurrentlyApplicableContentTypes, selectMixedContentFilters, selectProjectSpaceContentTypes, selectSpecificMixedContentFilterClause } from '../../../selectors/ContentFilterSelectors';
import { selectSpaceInfo } from '../../../selectors/ContentSelectors';
import { ServiceLocator } from '../../../ServiceLocator';
import { FilterMap } from '../../../state/ContentExplorerState';
import { isFavoritesSpace, isProjectSpace, isSearchSpace } from '../../../state/SpaceInfo';
import { UrlParamKeys } from '../../../UrlParamKeys';
import { updateOrderQueryParamsIfMismatched } from './SortContentSagas';

function isStayingOnMixedContent(contentTypes: ReadonlyArray<ExplorableItemType>, previousContentTypes: ReadonlyArray<ExplorableItemType>): boolean {
  return previousContentTypes && previousContentTypes.length > 1 && contentTypes.length > 1;
}

export function* filterByContentType(action: ContentTypeFilterSelectedAction) {
  try {
    // using selectContentTypeFilterValues instead of selectCurrentlyApplicableContentTypes here
    // because selectCurrentlyApplicableContentTypes returns defaults if content types are null
    const previousContentTypes = yield select(selectProjectSpaceContentTypes);
    const spaceInfo = yield select(selectSpaceInfo);

    // only send out the state update if there is something different or we've got null types.
    // This prevents the ContentCounts from incorrectly setting counts-up-to-date to false when we
    // haven't actually updated the content types due to logic in ContentTypeReducer
    if (!previousContentTypes || !shallowEqual(previousContentTypes, action.contentTypes)) {

      yield put(filterContentType(action.contentTypes));

      // if we are moving from mixed content to mixed content
      // We know the counts can't change so avoid spinners and making count requests
      if (!action.clearAndSet && (isProjectSpace(spaceInfo) || isFavoritesSpace(spaceInfo))
        && isStayingOnMixedContent(action.contentTypes, previousContentTypes)) {
          yield put(contentCountsUpToDate());
      }
    }

    updateOrderQueryParamsIfMismatched(action.contentTypes, spaceInfo);

    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* applyMixedContentFilter(action: ContentFilterAppliedAction) {
  try {
    const currentFilter: IStringValuedFilterClause = yield select(selectSpecificMixedContentFilterClause, action.filterType);
    if (doesActionCreateDifferentFilter(currentFilter, action)) {
      yield call(handleInterdependentFilters, action);
      yield put(filterMixedContent(action.filterType, action.value));
      yield put(requestSpaceContents());
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}

export function* setContentFilterNoRequestContent(action: SetContentFilterNoRequestContentAction) {
  try {
    const currentFilter: IStringValuedFilterClause = yield select(selectSpecificMixedContentFilterClause, action.filterType);
    if (doesActionCreateDifferentFilter(currentFilter, action)) {
      yield put(filterMixedContent(action.filterType, action.value));
    }
  } catch (e) {
    yield put(requestFailed(e, Messages.unexpectedError()));
  }
}


/**
 * Handles cases in which other filter actions need to be dispatched when a
 * filter changes.
 *
 * For example: A user may decide to filter exclusively on system jobs using a
 * job type dropdown but the system jobs are hidden due to the "show system
 * jobs" checkbox being unticked. In that case this method ticks that checkbox.
 *
 * @param action The ContentFilterAppliedAction to handle interdependent filters
 *  for. Does not apply the filter in the action.
 */
export function* handleInterdependentFilters(action: ContentFilterAppliedAction) {
  const filters: FilterMap = yield select(selectMixedContentFilters);
  const filter = filters[action.filterType] as IValuedFieldFilterClause<any>;
  const isNoopAction = !_.isUndefined(filter) && doesActionCreateSameFilter(filter, action)
    || _.isUndefined(filter) && action.value === undefined;
  if (isNoopAction) {
    return; // Do nothing
  }
  const isDelete = !_.isUndefined(filter) && action.value === undefined;
  switch (action.filterType) {
    case 'showSystemJobs':
      const onlySystemJobsFilter = Filter.stringFieldEq(
        FilterFieldNames.taskType, BackgroundJobTaskType.System);
      const isOnlyShowingSystemJobs = shallowEqual(filters.backgroundJobTaskType, onlySystemJobsFilter);
      // If we are no longer showing system jobs then we cannot be filtering exclusively on them
      if (isDelete && isOnlyShowingSystemJobs) {
        yield put(updateFiltersInBothUrlAndStateAction(ContentFiltersKeys.backgroundJobTaskType, undefined));
      }
      break;
    case 'backgroundJobTaskType':
      const filteringExclusivelyOnSystemJobs = action.value === BackgroundJobTaskType.System;
      // If we are filtering exclusively on system jobs we need to show them
      if (filteringExclusivelyOnSystemJobs) {
        yield put(updateFiltersInBothUrlAndStateAction(ContentFiltersKeys.showSystemJobs, true));
      }
      break;
  }
}

/**
 * Sets the url to match the new filter value, as well as dispatching an action
 * to update the redux store with the new filter value
 * @param action The UpdateFiltersInBothUrlAndStateAction to update the URL and
 * set the redux filter state for
 */
export function* updateFiltersInBothUrlAndState(action: UpdateFiltersInBothUrlAndStateAction) {
  // This function isn't automatically called whenever a filter is modified because
  // very often the filter modification happens _because_ the URL was modified

  // Instead we call it only when we know we need to modify the angular state as well
  // as modifying the redux filter state
  const locationService = ServiceLocator.get<ng.ILocationService>('$location');
  const value: string | undefined = _.isUndefined(action.value) ? undefined : String(action.value);
  if(locationService.search()[action.filterType] === value) {
    return; // No-op
  }
  locationService.search(action.filterType, value);
  yield put(mixedContentFilterApplied(action.filterType, value));
}

// Borrowed from FilterPanelCtrl::clearAllFilters
export function clearMixedContentFilters() {
  const locationService = ServiceLocator.get<ng.ILocationService>('$location');

  // Remove each of the filter keys.
  const urlParams = locationService.search();
  const inSearchSpace = isSearchSpace(selectSpaceInfo(appStore.getState()));

  _.each(_.keys(urlParams), (key: string) => {
    if (_.has(ContentFiltersKeys, key)) {
      if (inSearchSpace && key === ContentFiltersKeys.search) {
        // don't remove the search filter in the search space.
        return;
      }
      locationService.search(key, null);
    }
  });

  // removeRelevanceFromOrder when showing only views/workbooks/datasources
  const contentTypes = selectCurrentlyApplicableContentTypes(appStore.getState());
  if (contentTypes.length === 1 && (contentTypes[0] === DatasourceDefinition.contentType
      || contentTypes[0] === WorkbookDefinition.contentType
      || contentTypes[0] === ViewDefinition.contentType)) {
    const orderValue = locationService.search()[UrlParamKeys.Order];
    if (orderValue) {
      // The user may have added other order params, so we only remove relevance.
      let order: ReadonlyArray<IOrderClause> = Order.fromInput(orderValue);
      order = Order.withoutField(order, OrderKeys.relevance);
      const serializedOrder = Order.serialize(order);
      // we want to remove the search query parameter if there is none, passing 'null' does that.
      locationService.search(UrlParamKeys.Order, serializedOrder === ''? null : serializedOrder);
    }
  }

  // when updating the url outside Angular, $apply is needed to update the window location.
  ServiceLocator.getAngularActionContext().scope.$apply();
}
